import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

/**
 * A floating layer anchored to an element, rendered through a portal.
 *
 * Every popover in this app hangs off a control that lives inside something
 * scrollable -- the filter panel's condition list, the grid's own scroll
 * container, a cell with `overflow-hidden`. An absolutely positioned child is
 * cropped by the nearest scrolling ancestor, which produced a value popup
 * clipped to 36 pixels: all 63 suggestions present in the DOM, none of them
 * reachable.
 *
 * Portalling to document.body with fixed positioning takes the layer out of
 * every ancestor's overflow. It flips above the anchor when there is no room
 * below and clamps to the viewport, so it is always fully on screen.
 *
 * Note for tests: jsdom has no layout engine, so `getBoundingClientRect()`
 * returns zeros and a clipped popover still passes every DOM-level assertion.
 * Visibility here can only be verified in a real browser -- see the
 * `toBeInViewport` checks in the Playwright suite.
 */

const MARGIN = 6;
const MIN_HEIGHT = 160;

export interface PopoverProps {
  readonly anchorRef: RefObject<HTMLElement | null>;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly width?: number;
  /** Align the layer's left edge to the anchor's left (start) or right (end). */
  readonly align?: 'start' | 'end';
  readonly role?: 'dialog' | 'menu' | 'listbox';
  readonly ariaLabel: string;
  readonly testId?: string;
}

interface Position {
  readonly left: number;
  readonly top?: number;
  readonly bottom?: number;
  readonly maxHeight: number;
}

export function Popover({
  anchorRef,
  onClose,
  children,
  width,
  align = 'start',
  role = 'dialog',
  ariaLabel,
  testId,
}: PopoverProps): JSX.Element | null {
  const layerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position | null>(null);

  const reposition = useCallback((): void => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const layerWidth = width ?? Math.max(rect.width, 220);

    const spaceBelow = viewportHeight - rect.bottom - MARGIN;
    const spaceAbove = rect.top - MARGIN;
    // Prefer below; flip only when below genuinely cannot hold the layer and
    // above is roomier.
    const placeAbove = spaceBelow < MIN_HEIGHT && spaceAbove > spaceBelow;

    const rawLeft = align === 'end' ? rect.right - layerWidth : rect.left;
    const left = Math.max(
      MARGIN,
      Math.min(rawLeft, viewportWidth - layerWidth - MARGIN),
    );

    setPosition(
      placeAbove
        ? {
            left,
            bottom: viewportHeight - rect.top + MARGIN / 2,
            maxHeight: Math.max(MIN_HEIGHT, spaceAbove),
          }
        : {
            left,
            top: rect.bottom + MARGIN / 2,
            maxHeight: Math.max(MIN_HEIGHT, spaceBelow),
          },
    );
  }, [anchorRef, align, width]);

  useLayoutEffect(() => {
    reposition();
  }, [reposition]);

  useEffect(() => {
    // `true` captures scrolls on any ancestor, not just the window.
    const onScroll = (): void => reposition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [reposition]);

  useEffect(() => {
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (layerRef.current?.contains(target)) return;
      // Clicking the anchor is the caller's toggle; do not double-handle it.
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [anchorRef, onClose]);

  if (typeof document === 'undefined') return null;

  const anchorWidth = anchorRef.current?.getBoundingClientRect().width ?? 0;

  return createPortal(
    <div
      ref={layerRef}
      role={role}
      aria-label={ariaLabel}
      data-testid={testId}
      data-popover="true"
      className="flex flex-col overflow-hidden rounded-sm border border-line-strong bg-surface"
      style={{
        position: 'fixed',
        zIndex: 90,
        left: position?.left ?? -9999,
        ...(position?.top !== undefined ? { top: position.top } : {}),
        ...(position?.bottom !== undefined ? { bottom: position.bottom } : {}),
        width: width ?? Math.max(anchorWidth, 220),
        maxHeight: position?.maxHeight ?? MIN_HEIGHT,
        boxShadow: '0 12px 34px var(--c-shadow)',
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
